import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import moment from 'moment'
import withAppconfig from '../AppContext'
import withPublicActivities from '../components/withPublicActivities'
import ActivityBoardView from '../components/ActivityBoardView'
import {
  getOffset,
  resolveWeekWindow,
  groupActivitiesByDate,
  formatWeekRange,
  resolveBoardTheme,
  resolveTenantLogo,
} from '../components/ViewUtils'

// Same forced locale as ReadOnlyScreen - the board is a Finnish poster.
const APPINITLANG = 'fi'
const TIMEZONEOFFSET = getOffset('Europe/Helsinki')
const DEFAULT_TITLE = 'VIIKKOTAPAHTUMAT'
const TV_REFRESH_SECS = 900

const clampInt = (v, lo, hi, dflt) => {
  const n = parseInt(v, 10)
  return n >= lo && n <= hi ? n : dflt
}

/**
 * Public, embeddable weekly activities board (/embed/activities).
 *
 * No navigation chrome, no auth. Two targets, one view:
 *   ?mode=embed (default)  website iframe; width from ?width= like /embed
 *   ?mode=tv               lobby screen; fills the viewport, shrinks to fit,
 *                          never scrolls, and re-reads the clock periodically
 *                          so it rolls into the next week on its own.
 *
 * Params: from, days, mode, width, scale, refresh, title.
 */
function ActivityBoardScreen({ appconfig, activities }) {
  const params = new URLSearchParams(window.location.search)

  const mode = params.get('mode') === 'tv' ? 'tv' : 'embed'
  const tv = mode === 'tv'
  const bodyWidth = clampInt(params.get('width'), 280, 1600, 900)
  const scaleParam = parseFloat(params.get('scale'))
  const scale = scaleParam >= 0.4 && scaleParam <= 3 ? scaleParam : 1
  const refreshSecs = clampInt(params.get('refresh'), 60, 86400, tv ? TV_REFRESH_SECS : 0)
  const title = (params.get('title') || '').trim().slice(0, 48) || DEFAULT_TITLE

  // `tick` exists so the window is RECOMPUTED periodically rather than frozen
  // at mount: a TV left running must roll from one week to the next by itself.
  // It deliberately does not re-fetch - firebaseConnect holds a live
  // subscription, so activity edits arrive on their own.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!refreshSecs) return undefined
    const id = setInterval(() => setTick((t) => t + 1), refreshSecs * 1000)
    return () => clearInterval(id)
  }, [refreshSecs])

  const today = moment.utc().add(Math.abs(TIMEZONEOFFSET), 'minutes').format('YYYY-MM-DD')
  const { from, to, days } = resolveWeekWindow(params.get('from'), params.get('days'), today)
  const groups = groupActivitiesByDate(activities, from, days, APPINITLANG)

  // TV mode must never scroll: shrink the single --ab-unit knob until the board
  // fits the viewport. Bounded, and re-run whenever the content changes.
  const rootRef = useRef(null)
  const [fit, setFit] = useState(1)
  useLayoutEffect(() => {
    if (!tv) return
    const el = rootRef.current
    if (!el) return
    let f = 1
    el.style.setProperty('--ab-unit', 'calc(1vh * ' + scale + ')')
    for (let i = 0; i < 14 && el.scrollHeight > el.clientHeight + 1 && f > 0.35; i++) {
      f -= 0.05
      el.style.setProperty('--ab-unit', 'calc(1vh * ' + scale * f + ')')
    }
    setFit(f)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tv, scale, groups.length, tick])

  const unit = tv
    ? 'calc(1vh * ' + scale * fit + ')'
    : Math.max(7, bodyWidth / 90) * scale + 'px'

  // Feature off for this tenant, or activities are behind a membership wall
  // (ActivitiesScreen.js gates those venues) - an unauthenticated board must
  // not list them.
  const walled = !appconfig.activities || appconfig.activitiesOnlyMembers

  // Rows link out of the iframe to the existing public detail route, matching
  // the share-link format ActivitiesScreen builds.
  const hrefFor = (activity) => {
    if (!activity || !activity.id) return null
    const base =
      'https://' +
      (appconfig.istest ? 'utest.cintoia.com' + appconfig.pathPrefix : appconfig.hosts[0])
    return base + '/activities/' + encodeURIComponent(activity.id)
  }

  return (
    <ActivityBoardView
      mode={mode}
      title={title}
      rangeLabel={formatWeekRange(from, to)}
      groups={walled ? [] : groups}
      loading={!walled && activities === null}
      theme={resolveBoardTheme(appconfig)}
      logo={resolveTenantLogo(appconfig)}
      unit={unit}
      bodyWidth={bodyWidth}
      lang={APPINITLANG}
      hrefFor={hrefFor}
      emptyText={walled ? '' : 'Ei tapahtumia tällä viikolla.'}
      rootRef={rootRef}
    />
  )
}

// withAppconfig must be outermost: withPublicActivities reads
// props.appconfig.accountkey to build its subscription.
export default withAppconfig(withPublicActivities(ActivityBoardScreen))
